import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { AnimatedAccordion } from '@/components/common/AnimatedAccordion';
import {
  ISharedNavigationItem,
  ISharedNavigationGroupConfig,
} from '@/constants/shared/navigation';
import { GlassNavItem } from './GlassNavItem';

const groupItems = (
  items: ISharedNavigationItem[],
): Record<string, ISharedNavigationItem[]> => {
  const result: Record<string, ISharedNavigationItem[]> = {};
  for (const item of items) {
    if (!result[item.group]) result[item.group] = [];
    result[item.group].push(item);
  }
  return result;
};

const findGroupForPath = (
  items: ISharedNavigationItem[],
  path: string,
  fallbackGroup: string,
): string => {
  const match = items.find(
    (item) => path === item.path || path.startsWith(item.path),
  );
  return match?.group ?? fallbackGroup;
};

interface GlassNavListProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  items: ISharedNavigationItem[];
  groupConfig: ISharedNavigationGroupConfig;
  initialExpandedGroup?: string;
}

export const GlassNavList: React.FC<GlassNavListProps> = React.memo(({
  currentPath,
  onNavigate,
  items,
  groupConfig,
  initialExpandedGroup,
}) => {
  const { colors } = useThemeColors();
  const grouped = useMemo(() => groupItems(items), [items]);
  const initialGroup = useMemo(
    () =>
      initialExpandedGroup ??
      findGroupForPath(items, currentPath, groupConfig.order[0] ?? ''),
    [initialExpandedGroup, items, currentPath, groupConfig.order],
  );

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(initialGroup ? [initialGroup] : []),
  );

  useEffect(() => {
    const activeGroup = findGroupForPath(
      items,
      currentPath,
      groupConfig.order[0] ?? '',
    );
    if (!activeGroup) return;
    setExpandedGroups((prev) => {
      if (prev.has(activeGroup)) return prev;
      const next = new Set(prev);
      next.add(activeGroup);
      return next;
    });
  }, [currentPath, items, groupConfig.order]);

  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  const handleNavigate = useCallback(
    (path: string) => onNavigate(path),
    [onNavigate],
  );

  const renderNavItems = useCallback(
    (groupItems: ISharedNavigationItem[]) =>
      groupItems.map((item) => {
        const isActive =
          currentPath === item.path || currentPath.startsWith(item.path);
        const Icon = item.icon;

        return (
          <GlassNavItem
            key={item.id}
            title={item.title}
            isActive={isActive}
            onPress={() => handleNavigate(item.path)}
            icon={
              Icon ? (
                <Icon
                  width={24}
                  height={24}
                  color={isActive ? colors.text.primary : colors.text.muted}
                />
              ) : null
            }
          />
        );
      }),
    [currentPath, handleNavigate, colors],
  );

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.navList}>
          {groupConfig.order.map((group) => {
            const sectionItems = grouped[group];
            if (!sectionItems || sectionItems.length === 0) return null;
            const isExpanded = expandedGroups.has(group);

            return (
              <AnimatedAccordion
                key={group}
                isExpanded={isExpanded}
                onToggle={() => toggleGroup(group)}
                containerStyle={styles.section}
                headerStyle={styles.sectionHeaderButton}
                contentStyle={styles.sectionItems}
                header={(() => {
                  const GroupIcon = groupConfig.icons?.[group];
                  const headerColor = isExpanded ? colors.text.secondary : colors.text.subtle;
                  return (
                    <View style={styles.sectionHeaderInner}>
                      {GroupIcon ? (
                        <GroupIcon width={20} height={20} color={headerColor} />
                      ) : null}
                      <Text style={[styles.sectionHeader, { color: headerColor }]}>
                        {groupConfig.labels[group] ?? group}
                      </Text>
                    </View>
                  );
                })()}
                chevron={
                  <RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN
                    width={16}
                    height={16}
                    color={
                      isExpanded
                        ? colors.text.secondary
                        : colors.text.subtle
                    }
                  />
                }
              >
                {renderNavItems(sectionItems)}
              </AnimatedAccordion>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
});

GlassNavList.displayName = 'GlassNavList';

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  navList: {
    width: '100%',
    gap: 8,
  },
  section: {
    width: '100%',
  },
  sectionHeaderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  sectionHeaderInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  sectionHeader: {
    ...REDESIGN_TYPOGRAPHY.label,
  },
  sectionItems: {
    width: '100%',
    gap: 10,
  },
});
